import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import { listTciRegistry } from "services/fde/fdeRegistryService";
import {
  findNodeByPromptId,
  getTciGovernanceProfile,
} from "services/fde/tciGovernanceService";
import { loadManagedDomains } from "services/fde/tciManagerService";
import { db } from "services/firebase";

const STATUS_COLORS = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  returned: "info",
};

function asDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value.seconds != null) return new Date(value.seconds * 1000);
  return null;
}

const ReadOnlyPre = ({ children }) => (
  <Box
    component="pre"
    sx={{
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      typography: "body2",
      fontFamily: "monospace",
      m: 0,
      p: 2,
      bgcolor: "action.hover",
      borderRadius: 1,
      maxHeight: 360,
      overflow: "auto",
    }}
  >
    {children || "—"}
  </Box>
);

const TciPromptQueryManagerPage = () => {
  const navigate = useNavigate();
  const { tciId, promptId } = useParams();
  const { user, isGlobalAdmin, userData, organizationId } = useAuth();
  const { isFdeDc, loading: fdeLoading } = useFdeRole();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [allowed, setAllowed] = useState(false);
  const [tciName, setTciName] = useState(tciId);
  const [promptLabel, setPromptLabel] = useState(promptId);
  const [p1, setP1] = useState(null);
  const [p3Text, setP3Text] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [testCases, setTestCases] = useState([]);

  const canAccess = isFdeDc || isGlobalAdmin;

  const loadPage = useCallback(async () => {
    if (!user?.uid || !institutionId || !tciId || !promptId || !canAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      if (!isGlobalAdmin) {
        const domains = await loadManagedDomains({ uid: user.uid, institutionId });
        const managedTciIds = [
          ...new Set(domains.flatMap((domain) => domain.tciIds || []).filter(Boolean)),
        ];
        if (!managedTciIds.includes(tciId)) {
          setAllowed(false);
          setLoading(false);
          return;
        }
      }
      setAllowed(true);

      const orgDocId = `org__${institutionId}__${promptId}`;
      const [p3Snap, profile, registry, candidateSnap, testSnap] = await Promise.all([
        db.collection("llmLocalPrompts").doc(orgDocId).get(),
        getTciGovernanceProfile({ institutionId, tciId }),
        listTciRegistry({ institutionId }),
        db
          .collection("promptCandidates")
          .where("institutionId", "==", institutionId)
          .where("promptId", "==", promptId)
          .get(),
        db
          .collection("promptTestCases")
          .where("promptId", "==", promptId)
          .where("isActive", "==", true)
          .get(),
      ]);

      let p1Data = null;
      if (isGlobalAdmin) {
        try {
          const p1Snap = await db.collection("llmGlobalPrompts").doc(promptId).get();
          p1Data = p1Snap.exists ? p1Snap.data() : null;
        } catch (err) {
          console.warn("[TciPromptQueryManagerPage] global prompt read skipped", err);
        }
      }
      setP1(p1Data);

      const tci = registry.find((row) => row.id === tciId);
      setTciName(tci?.name || tciId);

      const graphNode = findNodeByPromptId(profile?.promptGraph || [], promptId);
      setPromptLabel(graphNode?.label || p1Data?.featureName || promptId);

      setP3Text(p3Snap.exists ? p3Snap.data()?.organizationPrompt || "" : "");

      const candidateRows = candidateSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort(
          (a, b) =>
            (asDate(b.proposedAt)?.getTime() || 0) - (asDate(a.proposedAt)?.getTime() || 0),
        );
      setCandidates(candidateRows);
      setTestCases(testSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load prompt details.");
    } finally {
      setLoading(false);
    }
  }, [user?.uid, institutionId, tciId, promptId, canAccess, isGlobalAdmin]);

  useEffect(() => {
    if (!fdeLoading) loadPage();
  }, [loadPage, fdeLoading]);

  const breadcrumbs = useMemo(
    () => (
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard/tci-manager" underline="hover" color="inherit">
          TCI Manager
        </Link>
        <Link
          component={RouterLink}
          to={`/dashboard/tci-manager/tcis/${encodeURIComponent(tciId)}`}
          underline="hover"
          color="inherit"
        >
          Manage TCIs
        </Link>
        <Typography color="text.secondary">{tciName}</Typography>
        <Typography color="text.primary">{promptLabel}</Typography>
      </Breadcrumbs>
    ),
    [tciId, tciName, promptLabel],
  );

  if (fdeLoading || loading) return <Loading page />;

  if (!canAccess) {
    return (
      <Alert severity="warning">
        Prompt QueryManager is available to TCI Manager role holders.
      </Alert>
    );
  }

  if (!allowed) {
    return <Alert severity="warning">You do not manage this TCI.</Alert>;
  }

  return (
    <Box>
      {breadcrumbs}
      <DashboardPageHeader title={promptLabel} subtitle={`Prompt: ${promptId}`} />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Active Prompt" />
        <Tab label="Candidate Prompts" />
        <Tab label="Test Cases" />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={3}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreRounded />}>
              <Typography variant="subtitle1">Global baseline — read only</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {p1?.model && <Chip size="small" label={`model: ${p1.model}`} />}
                  {p1?.temperature != null && (
                    <Chip size="small" label={`temperature: ${p1.temperature}`} />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  systemPrompt
                </Typography>
                <ReadOnlyPre>{p1?.systemPrompt}</ReadOnlyPre>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                    contextProvided
                  </AccordionSummary>
                  <AccordionDetails>
                    <ReadOnlyPre>{p1?.contextProvided}</ReadOnlyPre>
                  </AccordionDetails>
                </Accordion>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                    responseFormat
                  </AccordionSummary>
                  <AccordionDetails>
                    <ReadOnlyPre>{p1?.responseFormat}</ReadOnlyPre>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreRounded />}>
              <Typography variant="subtitle1">
                Your institution&apos;s rules — read only
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {p3Text ? (
                <ReadOnlyPre>{p3Text}</ReadOnlyPre>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No institution-specific rules set. Contact your org admin to add P3 rules.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={2}>
          {!candidates.length ? (
            <Typography color="text.secondary">
              No candidates have been generated for this prompt yet.
            </Typography>
          ) : (
            candidates.map((candidate) => {
              const proposedAt = asDate(candidate.proposedAt);
              const reviewedAt = asDate(candidate.reviewedAt);
              return (
                <Box key={candidate.id} sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      color={STATUS_COLORS[candidate.status] || "default"}
                      label={candidate.status}
                    />
                    {proposedAt && (
                      <Typography variant="caption" color="text.secondary">
                        {format(proposedAt, "MMM d, yyyy")}
                      </Typography>
                    )}
                  </Stack>
                  {candidate.diffSummary && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {candidate.diffSummary}
                    </Typography>
                  )}
                  {candidate.annotationCount != null && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {candidate.annotationCount} annotation(s)
                    </Typography>
                  )}
                  {candidate.status === "pending" && (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() =>
                        navigate(`/dashboard/tci-manager/candidates/${candidate.id}`)
                      }
                      sx={{ mt: 1 }}
                    >
                      Review candidate →
                    </Link>
                  )}
                  {candidate.status === "approved" && reviewedAt && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Approved {format(reviewedAt, "MMM d, yyyy")}
                      {candidate.reviewedBy ? ` · by ${candidate.reviewedBy}` : ""}
                    </Typography>
                  )}
                  {candidate.status === "rejected" && candidate.reviewNotes && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Rejected · {String(candidate.reviewNotes).slice(0, 100)}
                    </Typography>
                  )}
                  {candidate.status === "returned" && candidate.returnQuestions && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Returned · {String(candidate.returnQuestions).slice(0, 100)}
                    </Typography>
                  )}
                </Box>
              );
            })
          )}
        </Stack>
      )}

      {tab === 2 && (
        <Stack spacing={1}>
          {!testCases.length ? (
            <Typography color="text.secondary">
              No test cases have been defined for this prompt yet.
            </Typography>
          ) : (
            testCases.map((testCase) => (
              <Accordion key={testCase.id}>
                <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                  {testCase.description || testCase.id}
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {testCase.inputScenario}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                    {(testCase.expectedBehaviors || []).map((item) => (
                      <Chip key={item} size="small" color="success" label={item} />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(testCase.prohibitedBehaviors || []).map((item) => (
                      <Chip key={item} size="small" color="error" label={item} />
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Stack>
      )}
    </Box>
  );
};

export default TciPromptQueryManagerPage;
